import React, { useState } from 'react';
import { Doughnut, Bar } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement } from 'chart.js';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement);

const ApexTests = ({ apexTests }) => {
  const [showAllFailures, setShowAllFailures] = useState(false);

  if (!apexTests || !apexTests.result) {
    return null;
  }

  const { summary, tests } = apexTests.result;
  
  // Provide defaults if something is missing
  const {
    testsRan = 0,
    passing = 0,
    failing = 0,
    skipped = 0,
    passRate = '0%',
    failRate = '0%',
    testExecutionTime = '0 ms',
    testTotalTime = '0 ms',
    orgWideCoverage = '0%',
    testRunCoverage = '0%',
    outcome = 'Unknown'
  } = summary || {};

  const formatTime = (timeStr) => {
    if (!timeStr) return '0s';
    const ms = parseInt(timeStr.replace(/[^0-9]/g, '')) || 0;
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    if (minutes > 0) return `${minutes}m ${seconds}s`;
    return `${seconds}s`;
  };

  const failedTests = tests ? tests.filter(t => t.Outcome === 'Fail') : [];
  const displayedFailures = showAllFailures ? failedTests : failedTests.slice(0, 5);

  const doughnutData = {
    labels: ['Passing', 'Failing', 'Skipped'],
    datasets: [
      {
        data: [passing, failing, skipped],
        backgroundColor: [
          '#10B981', // green
          '#EF4444', // red
          '#F59E0B', // yellow
        ],
        borderWidth: 0,
      },
    ],
  };

  const doughnutOptions = {
    cutout: '75%',
    plugins: {
      legend: { display: false },
    },
    maintainAspectRatio: false,
  };

  const coverageToNumber = (covStr) => parseInt(covStr.replace('%', '')) || 0;
  const runCovNum = coverageToNumber(testRunCoverage);

  const coverageData = {
    labels: ['Test Run'],
    datasets: [
      {
        label: 'Coverage %',
        data: [runCovNum],
        backgroundColor: [
          runCovNum >= 75 ? '#10B981' : '#F59E0B',
        ],
        borderRadius: 4,
      }
    ]
  };

  const coverageOptions = {
    indexAxis: 'y',
    scales: {
      x: { max: 100, min: 0, ticks: { callback: (val) => val + '%' } }
    },
    plugins: {
      legend: { display: false }
    }
  };

  return (
    <div className="p-6 m-6 bg-white border border-gray-200 rounded-lg shadow-sm dark:border-gray-700 dark:bg-gray-800">
      <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-100 dark:border-gray-700">
        <div className="text-2xl">🧪</div>
        <h2 className="text-xl font-bold text-gray-900 dark:text-white m-0">Apex Tests Results</h2>
        <span className={`ml-auto px-3 py-1 text-sm font-semibold rounded-full ${
          outcome === 'Failed' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' 
          : 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
        }`}>
          {outcome}
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {/* Test Summary Widget */}
        <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-xl border border-gray-100 dark:border-gray-600">
          <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 mb-4 uppercase tracking-wider">Test Summary</h3>
          <div className="flex items-center justify-center h-48 relative">
            <div className="w-full h-full relative">
              <Doughnut data={doughnutData} options={doughnutOptions} />
            </div>
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <span className="text-5xl font-bold text-gray-900 dark:text-white">{testsRan}</span>
              <span className="text-sm text-gray-500 dark:text-gray-400 mt-1">Total</span>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-2 text-center text-sm">
            <div className="bg-white dark:bg-gray-800 p-2 rounded shadow-sm border border-gray-100 dark:border-gray-700">
              <div className="text-green-600 font-bold">{passing}</div>
              <div className="text-gray-500 text-xs">Passed</div>
            </div>
            <div className="bg-white dark:bg-gray-800 p-2 rounded shadow-sm border border-gray-100 dark:border-gray-700">
              <div className="text-red-600 font-bold">{failing}</div>
              <div className="text-gray-500 text-xs">Failed</div>
            </div>
            <div className="col-span-2 text-xs text-gray-500 mt-2">
              Pass Rate: <span className="font-semibold">{passRate}</span> • Execution Time: <span className="font-semibold">{formatTime(testExecutionTime)}</span>
            </div>
          </div>
        </div>

        {/* Coverage Widget */}
        <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-xl border border-gray-100 dark:border-gray-600">
          <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 mb-4 uppercase tracking-wider">Code Coverage</h3>
          <div className="h-48">
            <Bar data={coverageData} options={coverageOptions} />
          </div>
          <div className="mt-4 space-y-3">
            <div className="flex justify-between items-center bg-white dark:bg-gray-800 p-3 rounded shadow-sm border border-gray-100 dark:border-gray-700">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Test Run</span>
              <span className={`text-sm font-bold ${runCovNum < 75 ? 'text-yellow-600' : 'text-green-600'}`}>{testRunCoverage}</span>
            </div>
          </div>
        </div>

        {/* Actionable Insights / Metrics */}
        <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-xl border border-gray-100 dark:border-gray-600 flex flex-col">
          <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 mb-4 uppercase tracking-wider">Metrics</h3>
          
          <div className="flex-1 space-y-4">
            <div className="flex items-start gap-3 p-3 bg-white dark:bg-gray-800 rounded-lg shadow-sm border-l-4 border-l-blue-500">
              <div className="text-xl">⏱️</div>
              <div>
                <div className="text-sm font-bold text-gray-900 dark:text-white">Execution Time</div>
                <div className="text-xs text-gray-500 mt-1">{formatTime(testExecutionTime)} (Total: {formatTime(testTotalTime)})</div>
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 bg-white dark:bg-gray-800 rounded-lg shadow-sm border-l-4 border-l-indigo-500">
              <div className="text-xl">🎯</div>
              <div>
                <div className="text-sm font-bold text-gray-900 dark:text-white">Success Rate</div>
                <div className="text-xs text-gray-500 mt-1">{passRate} tests passed out of {testsRan} total.</div>
              </div>
            </div>
            
            {failing > 0 && (
              <div className="flex items-start gap-3 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg shadow-sm border-l-4 border-l-red-500">
                <div className="text-xl">⚠️</div>
                <div>
                  <div className="text-sm font-bold text-red-800 dark:text-red-400">{failing} Failing Tests</div>
                  <div className="text-xs text-red-600 dark:text-red-300 mt-1">Require immediate attention to unblock deployments.</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Failed Tests List */}
      {failing > 0 && failedTests.length > 0 && (
        <div className="mt-8 border-t border-gray-200 dark:border-gray-700 pt-6">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <span className="w-2 h-6 bg-red-500 rounded-full inline-block"></span>
            Failing Tests Details
          </h3>
          
          <div className="space-y-4">
            {displayedFailures.map((test, idx) => (
              <div key={test.Id || idx} className="bg-red-50 dark:bg-gray-800 border border-red-200 dark:border-red-900/50 rounded-lg overflow-hidden transition-all hover:shadow-md">
                <div className="p-4 border-b border-red-100 dark:border-gray-700 bg-white dark:bg-gray-800/80 flex flex-col md:flex-row md:items-center justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 rounded text-xs font-bold bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400">FAIL</span>
                      <h4 className="font-bold text-gray-900 dark:text-white">{test.MethodName}</h4>
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">Class: <span className="font-mono text-gray-700 dark:text-gray-300">{test.ApexClass?.Name}</span></div>
                  </div>
                  <div className="text-xs font-mono text-gray-500 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
                    Run time: {test.RunTime}ms
                  </div>
                </div>
                
                <div className="p-4 space-y-3 text-sm">
                  {test.Message && (
                    <div>
                      <strong className="text-gray-700 dark:text-gray-300 block mb-1">Error Message:</strong>
                      <div className="font-mono text-red-600 dark:text-red-400 bg-white dark:bg-gray-900 p-2 rounded border border-red-100 dark:border-red-900/30 overflow-x-auto whitespace-pre-wrap">
                        {test.Message}
                      </div>
                    </div>
                  )}
                  {test.StackTrace && (
                    <div>
                      <strong className="text-gray-700 dark:text-gray-300 block mb-1">Stack Trace:</strong>
                      <div className="font-mono text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-900 p-2 rounded border border-gray-200 dark:border-gray-700 overflow-x-auto whitespace-pre-wrap text-xs">
                        {test.StackTrace}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
          
          {failedTests.length > 5 && (
            <button 
              onClick={() => setShowAllFailures(!showAllFailures)}
              className="mt-4 w-full py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 font-medium rounded-lg transition-colors text-sm"
            >
              {showAllFailures ? 'Show Less' : `Show All ${failedTests.length} Failures`}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default ApexTests;